// Keyboard-aware focused-field reveal for a single owned scroll container.
//
// When the normalized viewport state reports the keyboard is visible and the
// focused element inside the supplied scroll container falls outside the usable
// region, this hook performs ONE minimal vertical correction on that container so
// the field becomes visible. It never scrolls the document or any other ancestor,
// never moves focus, never remounts content, and never overrides a user's own
// scrolling after the one-time reveal.
//
// Geometry is owned by ViewportProvider: this hook consumes the normalized
// `useViewport()` state only — it does not read the CSS variable, the raw native
// event, browser viewport APIs, the device pixel ratio, or use any observer.
//
// The minimal-correction / union / clamping decision itself lives in the pure,
// framework-free `revealMath` core, shared with the document-scroll adapter so
// their geometry rules cannot diverge. This hook remains the ELEMENT-container
// adapter: it measures the band and target against the supplied element and scrolls
// only that element.
//
// @docs:
// - docs/planning/MY_POLARIS_KEYBOARD_GEOMETRY.md

import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useViewport } from '../../providers/ViewportProvider';
import { computeReveal, type VerticalInterval } from './revealMath';

/** Narrow a measured rectangle to the vertical interval the shared core consumes. */
function rectToInterval(r: DOMRect): VerticalInterval {
  return { top: r.top, bottom: r.bottom };
}

export interface RevealFocusedFieldOptions {
  /** Breathing space (CSS px) kept above the revealed element. */
  topMargin?: number;
  /** Breathing space (CSS px) kept below the revealed element (also covers a
   *  reserved/sticky bottom area). */
  bottomMargin?: number;
  /** Master switch; when false the hook is inert. */
  enabled?: boolean;
  /**
   * Optional element that must remain visible together with the focused field.
   * When supplied and contained by the scroll container, the reveal target is the
   * union of the focused element and this companion (e.g. a submit button). Omit or
   * pass a null/outside ref to keep the field-only behaviour. The hook stays generic
   * — it holds no knowledge of what the companion is.
   */
  companionRef?: RefObject<HTMLElement | null>;
}

const DEFAULT_TOP_MARGIN = 8;
const DEFAULT_BOTTOM_MARGIN = 16;

type RevealKey = { el: Element; comp: Element | null; uh: number } | null;

/**
 * Reveal the focused element within `containerRef` when the keyboard reduces the
 * usable region. With `options.companionRef`, the reveal target becomes the union
 * of the focused element and the companion so both stay visible together. Corrective
 * scrolling is gated on `keyboardVisible` and applied at most once per (focused
 * element, companion, usable-height) combination, so duplicate geometry and
 * unrelated re-renders never re-scroll, and manual scrolling after the reveal is
 * preserved.
 */
export function useRevealFocusedField(
  containerRef: RefObject<HTMLElement | null>,
  options: RevealFocusedFieldOptions = {},
): void {
  const {
    topMargin = DEFAULT_TOP_MARGIN,
    bottomMargin = DEFAULT_BOTTOM_MARGIN,
    enabled = true,
    companionRef,
  } = options;

  // The provider is the sole geometry authority; react to its normalized state.
  const { keyboardVisible, usableHeightCssPx } = useViewport();

  // The (element, usable-height) that was last revealed. Dedupes repeat work and
  // — because it is only consulted on a focus change or a viewport transition —
  // leaves the user's own scrolling untouched.
  const lastKeyRef = useRef<RevealKey>(null);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;

    const cancel = () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };

    const commit = () => {
      frameRef.current = null;
      const c = containerRef.current;
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      if (!c || !active || active === c || !c.contains(active)) return;

      // Band and target geometry are measured here against this owned element; the
      // shared pure core (revealMath) decides the minimal, clamped correction so the
      // element and document adapters cannot drift apart. Companion inclusion still
      // requires the companion to live inside this container; the raw companion (pre-
      // containment) remains the dedupe identity below, preserving prior behaviour.
      const cRect = c.getBoundingClientRect();
      const companion = companionRef?.current ?? null;
      const includeCompanion = !!companion && companion !== active && c.contains(companion);

      const { scroll, changed } = computeReveal({
        band: { top: cRect.top + topMargin, bottom: cRect.bottom - bottomMargin },
        field: rectToInterval(active.getBoundingClientRect()),
        companion: includeCompanion ? rectToInterval(companion!.getBoundingClientRect()) : null,
        scroll: c.scrollTop,
        maxScroll: c.scrollHeight - c.clientHeight,
      });

      if (changed) c.scrollTop = scroll; // only this container's vertical offset; scrollLeft untouched
      lastKeyRef.current = { el: active, comp: companion, uh: usableHeightCssPx };
    };

    const maybeReveal = () => {
      if (!enabled) return;
      // Keyboard hidden: never correct, and reset the dedupe so a later open cycle
      // can reveal again.
      if (!keyboardVisible) {
        lastKeyRef.current = null;
        return;
      }
      const c = containerRef.current;
      if (!c || typeof document === 'undefined') return;
      const active = document.activeElement;
      if (!active || active === c || active === document.body || !c.contains(active)) return;

      const companion = companionRef?.current ?? null;
      const last = lastKeyRef.current;
      // Dedupe on the (focused element, companion, usable-height) triple so a
      // companion change re-evaluates but duplicate geometry / unrelated re-renders
      // and later manual scrolling do not.
      if (last && last.el === active && last.comp === companion && last.uh === usableHeightCssPx) return;

      // Measure after the CSS-variable-driven container resize has settled.
      cancel();
      frameRef.current = window.requestAnimationFrame(commit);
    };

    // Trigger 1: focus change within the owned container (delegated, not global).
    container?.addEventListener('focusin', maybeReveal);
    // Trigger 2: a viewport transition (this effect re-runs when keyboardVisible or
    // usableHeightCssPx changes).
    maybeReveal();

    return () => {
      container?.removeEventListener('focusin', maybeReveal);
      cancel();
    };
  }, [containerRef, companionRef, keyboardVisible, usableHeightCssPx, enabled, topMargin, bottomMargin]);
}

export default useRevealFocusedField;
